import Alpine from "alpinejs";
import mask from "@alpinejs/mask";
import tippy, { followCursor, hideAll } from "tippy.js";
import "@hotwired/turbo-rails";
import * as d3 from "d3";
import {
  BarChart,
  BumpChart,
  LineChart,
  PieChart,
  ScatterChart,
  ScatterTimeChart,
  StackedBarChart,
  StackedAreaChart,
} from "@get-dx/d3-charts";
import { convertMarkdownToHtml } from "@/shared/utils";
import { downloadAsImage } from "@/application/components/downloadAsImage";
import { pluralizeWithCount } from "@/lib/utils";
import { trackWebVitals } from "@/lib/web-vitals";

import "tippy.js/dist/tippy.css";
import "tippy.js/dist/border.css";

import "@/application/textarea_autoresize";
import "@/application/disable_enter";
import "@/application/userSearch";
import "@/application/snapshotHeatmap";
import "@/application/gauge";
import "@/application/accountAvatar";
import "@/application/pluralize";
import "@/application/scrubHtml";
import "@/application/pluralizeWithCount";
import "@/application/highlightText";
import "@/application/textDecorators";
import "@/application/targetRoleText";

// Magics
import { timeago, timeagoAbbreviated } from "@/application/magics/timeago";

// Custom Directives
import {
  tooltip,
  tooltipDelay,
  tooltipDelayArrow,
  tooltipIfTruncated,
} from "@/application/directives/tooltip";
import {
  disableOnClick,
  disableOnValidClick,
} from "@/application/directives/disableOnClick";
import { disableEnterSubmit } from "@/application/directives/disableEnterSubmit";

// Data Directives
import { accountSwitcher } from "@/application/components/accountSwitcher";
import { adminOrgCsvMappings } from "@/application/components/org_csv/adminOrgCsvMappings";
import { adminOrgCsvPreview } from "@/application/components/org_csv/adminOrgCsvPreview";
import { teamReview } from "@/application/components/org_csv/teamReview";
import { workdayMapping } from "@/application/components/org_csv/workdayMapping";
import { processingOrgCsv } from "@/application/components/org_csv/processingOrgCsv";
import { mappingValidator } from "@/application/components/user_csv/mappingValidator";
import { adminTagGroups } from "@/application/components/adminTagGroups";
import { adminDbAccess } from "@/application/components/adminDbAccess";
import { asyncSearchSelect } from "@/application/components/asyncSearchSelect";
import { areaSelect } from "@/application/components/areaSelect";
import { atlasArticle } from "@/application/components/atlasArticle";
import { atlasCatalog } from "@/application/components/atlasCatalog";
import { atlasEditor } from "@/application/components/atlasEditor";
import { bulkNudge } from "@/application/components/bulkNudge";
import { chartContainer } from "@/application/components/chartContainer";
import { costEstimator } from "@/application/components/costEstimator";
import { customBehavioralQuestions } from "@/application/components/customBehavioralQuestions";
import { customSelect } from "@/application/components/customSelect";
import { datacloudFilters } from "@/application/components/datacloudFilters";
import { datacloudSchema } from "@/application/components/datacloudSchema";
import { datacloudConnectionPurge } from "@/application/components/datacloudConnectionPurge";
import { snapshotDxiBreakdown } from "@/application/components/snapshotDxiBreakdown";
import { dirtyForm } from "@/application/components/dirtyForm";
import { dataFileUpload } from "@/application/components/dataFileUpload";
import { emailConfirmation } from "@/application/components/emailConfirmation";
import { filterMultiselect } from "@/application/components/filterMultiselect";
import { filterMultiselectPaginated } from "@/application/components/filterMultiselectPaginated";
import { flaggedComments } from "@/application/components/flaggedComments";
import { heatmapNavigator } from "@/application/components/heatmapNavigator";
import { histogramChart } from "@/application/components/histogramChart";
import { groupedMultiselect } from "@/application/components/groupedMultiselect";
import { insightsTeamFilter } from "@/application/components/insightsTeamFilter";
import { intercomBarChart } from "@/application/components/intercomBarChart";
import { intercomFilters } from "@/application/components/intercomFilters";
import { intercomLineChart } from "@/application/components/intercomLineChart";
import { internalTool } from "@/application/components/internalTool";
import { keyDriversChart } from "@/application/components/keyDriversChart";
import { livePreview } from "@/application/components/livePreview";
import { notifications } from "@/application/components/notifications";
import { onboardingChart } from "@/application/components/onboardingChart";
import { messagePreview } from "@/application/components/messagePreview";
import { snapshotCompareTo } from "@/application/components/snapshotCompareTo";
import { aggregationDropdown } from "@/application/components/aggregationDropdown";
import { snapshotCsat } from "@/application/components/snapshotCsat";
import { csatComment } from "@/application/components/csatComment";
import { snapshotDelivery } from "@/application/components/snapshotDelivery";
import { snapshotAttrition } from "@/application/components/snapshotAttrition";
import { snapshotIntakeAttributes } from "@/application/components/snapshotIntakeAttributes";
import { snapshotIntakeRating } from "@/application/components/snapshotIntakeRating";
import { snapshotIntakeRanking } from "@/application/components/snapshotIntakeRanking";
import { snapshotIntakeOutcomes } from "@/application/components/snapshotIntakeOutcomes";
import { snapshotIntakeInternalToolGroups } from "@/application/components/snapshotIntakeInternalToolGroups";
import { snapshotIntakeInternalTools } from "@/application/components/snapshotIntakeInternalTools";
import { snapshotIntakeBehaviors } from "@/application/components/snapshotIntakeBehaviors";
import { snapshotIntakeAllocation } from "@/application/components/snapshotIntakeAllocation";
import { snapshotIntakeResearch } from "@/application/components/snapshotIntakeResearch";
import { snapshotRequestUpdate } from "@/application/components/snapshotRequestUpdate";
import { snapshotResponse } from "@/application/components/snapshotResponse";
import { snapshotResponseCountDetails } from "@/application/components/snapshotResponseCountDetails";
import { snapshotResultsLineChart } from "@/application/components/snapshotResultsLineChart";
import { snapshotSetupBehavioralQuestions } from "@/application/components/snapshotSetupBehavioralQuestions";
import { snapshotSentiment } from "@/application/components/snapshotSentiment";
import { snapshotSetup } from "@/application/components/snapshotSetup";
import { snapshotSetupCadenceAndDrivers } from "@/application/components/snapshotSetupCadenceAndDrivers";
import { snapshotSetupInternalTools } from "@/application/components/snapshotSetupInternalTools";
import { snapshotSetupCustomAttributes } from "@/application/components/snapshotSetupCustomAttributes";
import { snapshotSetupAllocation } from "@/application/components/snapshotSetupAllocation";
import { snapshotSetupNudges } from "@/application/components/snapshotSetupNudges";
import { snapshotSetupProgram } from "@/application/components/snapshotSetupProgram";
import { snapshotWorkflows } from "@/application/components/snapshotWorkflows";
import { snapshotWorkflowsTimeCost } from "@/application/components/snapshotWorkflowsTimeCost";
import { snapshotAllocationResult } from "@/application/components/snapshotAllocationResult";
import { allocationQuestionConfig } from "@/application/components/allocationQuestionConfig";
import { selfReportedTag } from "@/application/components/selfReportedTag";
import { snapshotResultsNav } from "@/application/components/snapshotResultsNav";
import { snapshotStatus } from "@/application/components/snapshotStatus";
import { snapshotStatusV2 } from "@/application/components/snapshotStatusV2";
import { snapshotStatusMetadata } from "@/application/components/snapshotStatusMetadata";
import { tagUsersTable } from "@/application/components/tagUsersTable";
import { triageSquadViewModal } from "@/application/components/triageSquadViewModal";
import { triageResultsModal } from "@/application/components/triageResultsModal";
import { triageImprovementsModal } from "@/application/components/triageImprovementsModal";
import { snapshotQuickjump } from "@/application/components/snapshotQuickjump";
import { squadMultiselect } from "@/application/components/squadMultiselect";
import { squadQuickjump } from "@/application/components/squadQuickjump";
import { squadsTree } from "@/application/components/squadsTree";
import { participationTrend } from "@/application/components/participationTrend";
import { intercomStudyForm } from "@/application/components/intercomStudyForm";
import { multiselectV2 } from "@/application/components/multiselectV2";
import { teamsList } from "@/application/components/teamsList";
import { updateSnapshotSettings } from "@/application/components/updateSnapshotSettings";
import { userCsvUpload } from "@/application/components/userCsvUpload";
import { versionedTeamsTree } from "@/application/components/versionedTeamsTree";
import { userDetail } from "@/application/components/userDetail";
import { workdayRaasForm } from "@/application/components/workdayRaasForm";
import { userMultiselect } from "@/application/components/userMultiselect";
import { userMultiselectV2 } from "@/application/components/userMultiselectV2";
import { multiSelect } from "@/application/components/multiSelect";
import { userProfile } from "@/application/components/userProfile";
import { userSelect } from "@/application/components/userSelect";
import { commentEditor } from "@/application/components/commentEditor";
import { workflowResultDetails } from "@/application/components/workflowResultDetails";
import { workflowOverviewTabs } from "@/application/components/workflowOverviewTabs";
import { studiesAudienceSetup } from "@/application/components/studies/studiesAudienceSetup";
import { studiesSurveySetup } from "@/application/components/studies/studiesSurveySetup";
import { studyIntakeForm } from "@/application/components/studies/studyIntakeForm";
import { studiesResponseSegmentSelect } from "@/application/components/studies/studiesResponseSegmentSelect";
import { studiesTable } from "@/application/components/studies/studiesTable";
import { sortableList } from "@/application/components/sortableList";
import { configureStudyTriggers } from "@/application/components/intercom/configureStudyTriggers";
import { snapshotBranchConfig } from "@/application/components/snapshotBranchConfig";
import { snapshotAiOverview } from "@/application/components/snapshotAiOverview";
import { hotspotAnalysis } from "@/components/hotspotAnalysis";
import { userRoles } from "@/application/components/userRoles";
import { peopleFilter } from "@/application/components/peopleFilter";
import { aiCodeMetrics } from "@/application/components/admin/aiCodeMetrics";
import { aiCodeMetricsOtp } from "@/application/components/admin/aiCodeMetricsOtp";
import { editConnectionForm } from "@/application/components/admin/connections/editConnectionForm";
import { editApiCredentialsForm } from "@/application/components/admin/connections/editApiCredentialsForm";
import { credentialsManager } from "@/application/components/admin/connections/credentialsManager";
import {
  runStepByStepVerification,
  runCredentialsV2Flow,
} from "@/application/components/admin/connections/connectionVerification";
import { copyableValue } from "@/application/components/copyableValue";

// Stores
import { snapshotResultFiltersStore } from "@/application/components/snapshotResultFiltersStore";
import { triageSquadManagerStore } from "@/application/components/triageSquadManagerStore";
import { pushNotification } from "@/application/pushNotification";

import { userAvatar } from "@/application/userAvatar";
import { initialsAvatar } from "@/application/initialsAvatar";
import { chartTooltipHtml } from "@/application/chartTooltipHtml";
import { ack } from "@/application/ack";
import { applyPrLabel } from "@/lib/utils";
import { trackEvent } from "@/application/trackEvent";

Alpine.plugin(mask);

tippy.setDefaultProps({
  plugins: [followCursor],
  theme: "dx",
});

tippy.hideAll = hideAll;

// Window objects and document listeners
window.tippy = tippy;
window.Alpine = Alpine;
window.d3 = d3;
window.BarChart = BarChart;
window.BumpChart = BumpChart;
window.LineChart = LineChart;
window.PieChart = PieChart;
window.ScatterChart = ScatterChart;
window.ScatterTimeChart = ScatterTimeChart;
window.StackedBarChart = StackedBarChart;
window.StackedAreaChart = StackedAreaChart;
window.pushNotification = pushNotification;
window.userAvatar = userAvatar;
window.initialsAvatar = initialsAvatar;
window.chartTooltipHtml = chartTooltipHtml;
window.ack = ack;
window.applyPrLabel = applyPrLabel;
window.trackEvent = trackEvent;
window.downloadAsImage = downloadAsImage;
window.convertMarkdownToHtml = convertMarkdownToHtml;
window.pluralizeWithCount = pluralizeWithCount;
window.tooltipIfTruncated = tooltipIfTruncated;
window.runStepByStepVerification = runStepByStepVerification;
window.runCredentialsV2Flow = runCredentialsV2Flow;

// Magics
Alpine.magic("timeagoAbbreviated", timeagoAbbreviated);
Alpine.magic("timeago", timeago);

// Custom Directives
Alpine.directive("tooltip", tooltip);
Alpine.directive("tooltip-delay", tooltipDelay);
Alpine.directive("tooltip-delay-arrow", tooltipDelayArrow);
Alpine.directive("disable-on-click", disableOnClick);
Alpine.directive("disable-on-valid-click", disableOnValidClick);
Alpine.directive("disable-enter-submit", disableEnterSubmit);

// Data Directives
Alpine.data("accountSwitcher", accountSwitcher);
Alpine.data("adminOrgCsvMappings", adminOrgCsvMappings);
Alpine.data("adminOrgCsvPreview", adminOrgCsvPreview);
Alpine.data("teamReview", teamReview);
Alpine.data("workdayMapping", workdayMapping);
Alpine.data("processingOrgCsv", processingOrgCsv);
Alpine.data("mappingValidator", mappingValidator);
Alpine.data("adminTagGroups", adminTagGroups);
Alpine.data("adminDbAccess", adminDbAccess);
Alpine.data("asyncSearchSelect", asyncSearchSelect);
Alpine.data("areaSelect", areaSelect);
Alpine.data("atlasArticle", atlasArticle);
Alpine.data("atlasCatalog", atlasCatalog);
Alpine.data("atlasEditor", atlasEditor);
Alpine.data("bulkNudge", bulkNudge);
Alpine.data("chartContainer", chartContainer);
Alpine.data("costEstimator", costEstimator);
Alpine.data("customSelect", customSelect);
Alpine.data("customBehavioralQuestions", customBehavioralQuestions);
Alpine.data("datacloudFilters", datacloudFilters);
Alpine.data("datacloudSchema", datacloudSchema);
Alpine.data("datacloudConnectionPurge", datacloudConnectionPurge);
Alpine.data("dirtyForm", dirtyForm);
Alpine.data("dataFileUpload", dataFileUpload);
Alpine.data("snapshotDxiBreakdown", snapshotDxiBreakdown);
Alpine.data("emailConfirmation", emailConfirmation);
Alpine.data("filterMultiselect", filterMultiselect);
Alpine.data("filterMultiselectPaginated", filterMultiselectPaginated);
Alpine.data("flaggedComments", flaggedComments);
Alpine.data("heatmapNavigator", heatmapNavigator);
Alpine.data("histogramChart", histogramChart);
Alpine.data("groupedMultiselect", groupedMultiselect);
Alpine.data("insightsTeamFilter", insightsTeamFilter);
Alpine.data("intercomBarChart", intercomBarChart);
Alpine.data("intercomFilters", intercomFilters);
Alpine.data("intercomLineChart", intercomLineChart);
Alpine.data("internalTool", internalTool);
Alpine.data("keyDriversChart", keyDriversChart);
Alpine.data("livePreview", livePreview);
Alpine.data("notifications", notifications);
Alpine.data("onboardingChart", onboardingChart);
Alpine.data("messagePreview", messagePreview);
Alpine.data("snapshotCompareTo", snapshotCompareTo);

Alpine.data("aggregationDropdown", aggregationDropdown);
Alpine.data("snapshotCsat", snapshotCsat);
Alpine.data("csatComment", csatComment);
Alpine.data("snapshotDelivery", snapshotDelivery);
Alpine.data("snapshotAttrition", snapshotAttrition);
Alpine.data("snapshotIntakeAttributes", snapshotIntakeAttributes);
Alpine.data("snapshotIntakeRating", snapshotIntakeRating);
Alpine.data("snapshotIntakeRanking", snapshotIntakeRanking);
Alpine.data("snapshotIntakeOutcomes", snapshotIntakeOutcomes);
Alpine.data("snapshotIntakeBehaviors", snapshotIntakeBehaviors);
Alpine.data(
  "snapshotIntakeInternalToolGroups",
  snapshotIntakeInternalToolGroups,
);
Alpine.data("snapshotIntakeInternalTools", snapshotIntakeInternalTools);
Alpine.data("snapshotIntakeAllocation", snapshotIntakeAllocation);
Alpine.data("snapshotIntakeResearch", snapshotIntakeResearch);
Alpine.data("snapshotRequestUpdate", snapshotRequestUpdate);
Alpine.data("snapshotResponse", snapshotResponse);
Alpine.data("snapshotResponseCountDetails", snapshotResponseCountDetails);
Alpine.data("snapshotResultsLineChart", snapshotResultsLineChart);
Alpine.data("snapshotSetup", snapshotSetup);
Alpine.data(
  "snapshotSetupBehavioralQuestions",
  snapshotSetupBehavioralQuestions,
);
Alpine.data("snapshotSentiment", snapshotSentiment);
Alpine.data("snapshotWorkflows", snapshotWorkflows);
Alpine.data("snapshotWorkflowsTimeCost", snapshotWorkflowsTimeCost);
Alpine.data("snapshotSetupCadenceAndDrivers", snapshotSetupCadenceAndDrivers);
Alpine.data("snapshotSetupInternalTools", snapshotSetupInternalTools);
Alpine.data("snapshotSetupCustomAttributes", snapshotSetupCustomAttributes);
Alpine.data("snapshotSetupAllocation", snapshotSetupAllocation);
Alpine.data("snapshotSetupNudges", snapshotSetupNudges);
Alpine.data("snapshotSetupProgram", snapshotSetupProgram);
Alpine.data("snapshotAllocationResult", snapshotAllocationResult);
Alpine.data("allocationQuestionConfig", allocationQuestionConfig);
Alpine.data("selfReportedTag", selfReportedTag);
Alpine.data("snapshotResultsNav", snapshotResultsNav);
Alpine.data("snapshotStatus", snapshotStatus);
Alpine.data("snapshotStatusV2", snapshotStatusV2);
Alpine.data("snapshotStatusMetadata", snapshotStatusMetadata);
Alpine.data("participationTrend", participationTrend);
Alpine.data("tagUsersTable", tagUsersTable);
Alpine.data("triageSquadViewModal", triageSquadViewModal);
Alpine.data("triageResultsModal", triageResultsModal);
Alpine.data("triageImprovementsModal", triageImprovementsModal);
Alpine.data("snapshotQuickjump", snapshotQuickjump);
Alpine.data("squadMultiselect", squadMultiselect);
Alpine.data("squadQuickjump", squadQuickjump);
Alpine.data("squadsTree", squadsTree);
Alpine.data("intercomStudyForm", intercomStudyForm);
Alpine.data("multiselectV2", multiselectV2);
Alpine.data("teamsList", teamsList);
Alpine.data("updateSnapshotSettings", updateSnapshotSettings);
Alpine.data("userCsvUpload", userCsvUpload);
Alpine.data("versionedTeamsTree", versionedTeamsTree);
Alpine.data("userDetail", userDetail);
Alpine.data("userMultiselect", userMultiselect);
Alpine.data("userMultiselectV2", userMultiselectV2);
Alpine.data("multiSelect", multiSelect);
Alpine.data("userProfile", userProfile);
Alpine.data("userSelect", userSelect);
Alpine.data("commentEditor", commentEditor);
Alpine.data("workflowResultDetails", workflowResultDetails);
Alpine.data("workflowOverviewTabs", workflowOverviewTabs);
Alpine.data("studiesAudienceSetup", studiesAudienceSetup);
Alpine.data("studiesSurveySetup", studiesSurveySetup);
Alpine.data("studyIntakeForm", studyIntakeForm);
Alpine.data("studiesResponseSegmentSelect", studiesResponseSegmentSelect);
Alpine.data("studiesTable", studiesTable);
Alpine.data("configureStudyTriggers", configureStudyTriggers);
Alpine.data("workdayRaasForm", workdayRaasForm);
Alpine.data("snapshotBranchConfig", snapshotBranchConfig);
Alpine.data("sortableList", sortableList);
Alpine.data("snapshotAiOverview", snapshotAiOverview);
Alpine.data("hotspotAnalysis", hotspotAnalysis);
Alpine.data("userRoles", userRoles);
Alpine.data("peopleFilter", peopleFilter);
Alpine.data("aiCodeMetrics", aiCodeMetrics);
Alpine.data("aiCodeMetricsOtp", aiCodeMetricsOtp);
Alpine.data("editConnectionForm", editConnectionForm);
Alpine.data("editApiCredentialsForm", editApiCredentialsForm);
Alpine.data("credentialsManager", credentialsManager);
Alpine.data("copyableValue", copyableValue);

// Stores
Alpine.store("snapshotResultFilters", snapshotResultFiltersStore);
Alpine.store("triageSquadManager", triageSquadManagerStore);

Alpine.start();

trackWebVitals();
